import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';
import { useEffect, useRef } from 'react';
import { logger } from '../utils/logger';
import { createChatNotification } from '../utils/chatNotifications';
import { normalizeAvatarUrl } from './useUserProfiles';

/**
 * Wrapper to add timeout to Supabase queries
 * Helps handle network errors like ERR_QUIC_PROTOCOL_ERROR
 */
async function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number = 15000,
  errorMessage: string = 'Request timeout'
): Promise<T> {
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error(`${errorMessage} (${timeoutMs}ms)`));
    }, timeoutMs);
  });

  return Promise.race([promise, timeout]);
}

/**
 * Check if error is a network error that should be retried
 */
function isNetworkError(error: any): boolean {
  if (!error) return false;
  const errorMessage = error.message || error.toString() || '';
  const errorCode = error.code || '';
  
  return (
    errorMessage.includes('ERR_QUIC_PROTOCOL_ERROR') ||
    errorMessage.includes('ERR_NETWORK') ||
    errorMessage.includes('ERR_INTERNET_DISCONNECTED') ||
    errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('NetworkError') ||
    errorMessage.includes('timeout') ||
    errorCode === 'PGRST301' || // Supabase timeout
    errorCode === 'PGRST302'    // Supabase connection error
  );
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_role: 'admin' | 'agent' | 'user' | 'carrier';
  sender_name: string;
  sender_email: string;
  sender_avatar_url?: string | null; // Normalized avatar URL of the sender
  message: string;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UseChatMessagesProps {
  conversationId: string | null;
  enabled?: boolean;
  // Optional React Query configuration overrides
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  refetchOnMount?: boolean;
  refetchInterval?: number | false;
}

/**
 * Hook to fetch and manage chat messages with Supabase Realtime
 * Uses React Query for caching and Supabase Realtime for live updates
 * FREE tier: Up to 2M messages/month
 */
export function useChatMessages({
  conversationId,
  enabled = true,
  staleTime = 10 * 1000,
  gcTime,
  refetchOnWindowFocus = true,
  refetchOnMount = true,
  refetchInterval = 30000,
}: UseChatMessagesProps) {
  const queryClient = useQueryClient();
  const channelRef = useRef<any>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const errorLogThrottleRef = useRef<Map<string, number>>(new Map());

  // Get current user ID
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      currentUserIdRef.current = user?.id || null;
    });
  }, []);

  // Fetch messages
  const { data: messages = [], isLoading, error } = useQuery({
    queryKey: ['chatMessages', conversationId],
    queryFn: async (): Promise<ChatMessage[]> => {
      if (!conversationId) return [];

      logger.log('📥 Fetching chat messages for conversation:', conversationId);

      try {
        const queryBuilder = supabase
          .from('chat_messages')
          .select('*')
          .eq('conversation_id', conversationId)
          .order('created_at', { ascending: true })
          .limit(100); // Last 100 messages

        const queryPromise = queryBuilder as unknown as Promise<{ data: ChatMessage[] | null; error: any }>;

        const result = await withTimeout<{ data: ChatMessage[] | null; error: any }>(
          queryPromise,
          15000, // 15 second timeout
          'Chat messages request timed out'
        );

        const { data, error: fetchError } = result;

        if (fetchError) {
          // Check if it's a network error
          if (isNetworkError(fetchError)) {
            logger.warn('⚠️ Network error fetching chat messages, will retry:', {
              error: fetchError.message,
              conversationId,
            });
          } else {
            logger.error('❌ Error fetching chat messages:', fetchError);
          }
          throw fetchError;
        }

        logger.log('✅ Fetched messages:', (data || []).length);
        return (data || []) as ChatMessage[];
      } catch (err: any) {
        // Re-throw network errors with more context
        if (isNetworkError(err)) {
          const networkError = new Error(
            `Connection issue: ${err.message || 'Network error. Please check your internet connection.'}`
          );
          (networkError as any).isNetworkError = true;
          throw networkError;
        }
        throw err;
      }
    },
    enabled: enabled && !!conversationId,
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchInterval,
    retry: (failureCount, error: any) => {
      // Retry up to 3 times for network errors, 1 time for other errors
      if (isNetworkError(error)) {
        return failureCount < 3;
      }
      return failureCount < 1;
    },
    retryDelay: (attemptIndex) => {
      // Exponential backoff: 1s, 2s, 4s
      return Math.min(1000 * Math.pow(2, attemptIndex), 4000);
    },
  });

  // Set up Realtime subscription
  useEffect(() => {
    if (!conversationId || !enabled) {
      logger.log('⏸️ Chat subscription disabled:', { conversationId, enabled });
      return;
    }

    logger.log('🔌 Setting up Realtime subscription for conversation:', conversationId);

    // Create channel for this conversation
    // IMPORTANT: Channel name must be unique per conversation
    // Using a more specific channel name to avoid conflicts
    const channelName = `chat:${conversationId}`;

    // Clean up any existing channel for this conversation to prevent conflicts
    if (channelRef.current) {
      logger.log('🧹 Cleaning up existing channel before creating new one:', channelName);
      try {
        supabase.removeChannel(channelRef.current);
      } catch (error) {
        logger.warn('⚠️ Error removing existing channel (non-critical):', error);
      }
      channelRef.current = null;
    }

    const channel = supabase
      .channel(channelName, {
        config: {
          // Realtime configuration for postgres_changes
          // Use broadcast mode to avoid binding mismatch errors
          broadcast: { self: false },
          presence: { key: '' },
        },
      })
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const newMessage = payload.new as ChatMessage;
          logger.log('📨 New chat message received via Realtime:', {
            messageId: newMessage.id,
            sender: newMessage.sender_name,
            senderId: newMessage.sender_id,
            conversationId,
            message: newMessage.message.substring(0, 50),
          });

          // FIRST: Update cache immediately for instant UI update
          queryClient.setQueryData<ChatMessage[]>(
            ['chatMessages', conversationId],
            (old = []) => {
              // Check if message already exists
              const exists = old.some((msg) => msg.id === newMessage.id);
              if (exists) {
                logger.log('⚠️ Message already in cache, updating:', newMessage.id);
                // Update existing message in case it changed
                return old.map((msg) => (msg.id === newMessage.id ? newMessage : msg));
              }
              const updated = [...old, newMessage].sort(
                (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
              );
              logger.log('✅ Message added to cache via Realtime, total messages:', updated.length);
              return updated;
            }
          );

          // THEN: Create notification asynchronously (non-blocking)
          // Use setTimeout to ensure cache update happens first
          setTimeout(() => {
            createChatNotification(newMessage, currentUserIdRef.current).catch((error) => {
              logger.error('Error creating chat notification:', error);
            });
          }, 0);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'chat_messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          logger.log('📝 Chat message updated via Realtime:', payload.new);
          // Update message in cache
          queryClient.setQueryData<ChatMessage[]>(
            ['chatMessages', conversationId],
            (old = []) =>
              old.map((msg) =>
                msg.id === (payload.new as ChatMessage).id
                  ? (payload.new as ChatMessage)
                  : msg
              )
          );
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          logger.log('✅ Successfully subscribed to chat channel:', {
            channelName,
            conversationId,
            status,
          });
        } else if (status === 'CHANNEL_ERROR') {
          // Throttle error logging to avoid spam (log once per 30 seconds per channel)
          const now = Date.now();
          const lastLogTime = errorLogThrottleRef.current.get(channelName) || 0;
          const shouldLog = now - lastLogTime > 30000; // 30 seconds
          
          if (shouldLog) {
            errorLogThrottleRef.current.set(channelName, now);
            
            // Handle undefined errors gracefully
            const errorDetails = err || { message: 'Unknown error', code: 'UNKNOWN' };
            // Type guard: check if error has 'code' property (Supabase error) or is standard Error
            const errorCode = (errorDetails && typeof errorDetails === 'object' && 'code' in errorDetails) 
              ? (errorDetails as { code: string }).code 
              : 'UNKNOWN';
            const errorMessage = errorDetails?.message || (errorDetails instanceof Error ? errorDetails.message : 'No error message provided');
            
            // Check if it's the binding mismatch error (known issue, not critical)
            const isBindingMismatch = errorMessage.includes('mismatch between server and client bindings');
            
            if (isBindingMismatch) {
              // This is a known Supabase Realtime issue that doesn't affect functionality
              // Log as warning instead of error to reduce noise
              logger.warn('⚠️ Realtime binding mismatch (non-critical):', {
                channelName,
                conversationId,
                note: 'This is a known Supabase Realtime issue. Chat functionality should still work via polling.',
              });
            } else {
              logger.error('❌ Chat channel error:', {
                status,
                error: errorDetails,
                errorMessage,
                errorCode,
                channelName,
                conversationId,
              });
              
              // Log additional context for debugging
              logger.warn('💡 Channel error troubleshooting:', {
                conversationId,
                channelName,
                suggestion: 'Check if Realtime is enabled for chat_messages table and RLS policies allow subscriptions',
              });
            }
          }
          
          // Don't try to resubscribe automatically for CHANNEL_ERROR
          // This usually indicates a configuration issue (RLS, Realtime not enabled, etc.)
          // The app will continue to work via polling (refetchInterval)
        } else if (status === 'TIMED_OUT') {
          logger.warn('⏱️ Chat channel timed out, attempting to resubscribe:', {
            channelName,
            conversationId,
          });
          // Try to resubscribe after a delay for timeout
          setTimeout(() => {
            logger.log('🔄 Attempting to resubscribe to channel:', channelName);
            try {
              channel.subscribe();
            } catch (resubError) {
              logger.error('❌ Error resubscribing:', resubError);
            }
          }, 5000);
        } else if (status === 'CLOSED') {
          logger.log('🔌 Chat channel closed:', { channelName, conversationId });
        } else {
          logger.log('🔄 Chat channel status:', { status, channelName, conversationId });
        }
      });

    channelRef.current = channel;

    return () => {
      logger.log('🧹 Cleaning up chat channel:', channelName);
      if (channelRef.current) {
        try {
          supabase.removeChannel(channelRef.current);
        } catch (error) {
          logger.error('Error removing channel:', error);
        }
        channelRef.current = null;
      }
    };
  }, [conversationId, enabled, queryClient]);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (message: string) => {
      if (!conversationId) throw new Error('No conversation ID');

      // Get current user info
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name, email, role, avatar_url')
        .eq('id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Determine sender role
      let senderRole: 'admin' | 'agent' | 'user' | 'carrier' = 'user';
      if (profile.role === 'admin') senderRole = 'admin';
      else if (profile.role === 'agent') senderRole = 'agent';
      else {
        // Check if user is a carrier
        const { data: carrier } = await supabase
          .from('carrier_registrations')
          .select('id')
          .eq('email', profile.email)
          .single();
        if (carrier) senderRole = 'carrier';
      }

      // Normalize avatar URL (same as Header.tsx)
      const normalizedAvatarUrl = normalizeAvatarUrl(profile.avatar_url);

      logger.log('📤 Sending message:', {
        conversationId,
        senderId: user.id,
        senderRole,
        senderAvatarUrl: normalizedAvatarUrl ? 'present' : 'missing',
        messageLength: message.trim().length,
      });

      const { data, error: insertError } = await supabase
        .from('chat_messages')
        .insert({
          conversation_id: conversationId,
          sender_id: user.id,
          sender_role: senderRole,
          sender_name: profile.full_name || profile.email,
          sender_email: profile.email,
          sender_avatar_url: normalizedAvatarUrl,
          message: message.trim(),
        })
        .select()
        .single();

      if (insertError) {
        logger.error('❌ Error sending chat message:', {
          error: insertError,
          conversationId,
          senderId: user.id,
          senderRole,
        });
        throw insertError;
      }

      logger.log('✅ Message sent successfully:', {
        messageId: data.id,
        conversationId: data.conversation_id,
        senderId: data.sender_id,
        senderRole: data.sender_role,
        message: data.message.substring(0, 50),
      });
      return data as ChatMessage;
    },
    onSuccess: (data) => {
      logger.log('✅ Message mutation successful, updating cache immediately');
      // FIRST: Immediately add to cache for instant UI feedback (optimistic update)
      // This ensures the sender sees their message instantly
      queryClient.setQueryData<ChatMessage[]>(
        ['chatMessages', conversationId],
        (old = []) => {
          const exists = old.some((msg) => msg.id === data.id);
          if (exists) {
            logger.log('⚠️ Message already in cache, skipping duplicate');
            return old;
          }
          const updated = [...old, data].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          logger.log('✅ Message added to cache immediately, total messages:', updated.length);
          return updated;
        }
      );

      // Also update the sender's profile cache if needed (for avatar display)
      // This ensures avatars are available immediately
      if (data.sender_id) {
        queryClient.invalidateQueries({ queryKey: ['userProfile', data.sender_id] });
        queryClient.invalidateQueries({ queryKey: ['userProfilesByIds'] });
      }

      // THEN: Invalidate as backup (Realtime should handle it, but this ensures consistency)
      // Note: We don't invalidate immediately to avoid flickering - Realtime will update
      // queryClient.invalidateQueries({ queryKey: ['chatMessages', conversationId] });
    },
    onError: (error) => {
      logger.error('❌ Message mutation failed:', error);
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (messageIds: string[]) => {
      if (!conversationId || messageIds.length === 0) return;

      const { error } = await supabase
        .from('chat_messages')
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
        })
        .in('id', messageIds)
        .eq('conversation_id', conversationId)
        .eq('is_read', false);

      if (error) {
        logger.error('Error marking messages as read:', error);
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate to refresh unread counts
      queryClient.invalidateQueries({ queryKey: ['chatMessages', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['unreadChatCount', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['adminChatConversations'] });
    },
  });

  return {
    messages,
    isLoading,
    error,
    sendMessage: sendMessageMutation.mutate,
    sendMessageAsync: sendMessageMutation.mutateAsync,
    isSending: sendMessageMutation.isPending,
    markAsRead: markAsReadMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
  };
}

/**
 * Hook to get unread message count for a conversation
 */
export function useUnreadMessageCount(conversationId: string | null) {
  return useQuery({
    queryKey: ['unreadChatCount', conversationId],
    queryFn: async (): Promise<number> => {
      if (!conversationId) return 0;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      const { count, error } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('conversation_id', conversationId)
        .eq('is_read', false)
        .neq('sender_id', user.id); // Don't count own messages

      if (error) {
        logger.error('Error counting unread messages:', error);
        return 0;
      }

      return count || 0;
    },
    enabled: !!conversationId,
    refetchInterval: 30000, // Check every 30 seconds
    staleTime: 10 * 1000, // 10 seconds
  });
}

